import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  Divider,
  Alert,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  PendingActions as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Store as StoreIcon,
  Edit as EditIcon,
  CompareArrows as CompareArrowsIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useSnackbar } from 'notistack';
import storeEditRequestService, {
  IStoreEditRequest,
  StoreEditRequestStatus
} from '../services/storeEditRequestService';
import { useAuth } from '../context/AuthContext';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import { ChangesList, ChangeItem } from '../components/product/EditRequestDiffComponents';
import { EditHistoryTimeline, EditRequestSummary } from '../components/product/EditHistoryTimeline';
import './StoreEditRequestsManagement.css';
import AssigneeCell from '../components/common/AssigneeCell';

const tabs = [
  { label: 'همه درخواست‌ها', value: undefined, icon: <FilterListIcon />, color: '' },
  { label: 'در انتظار بررسی', value: StoreEditRequestStatus.PENDING, icon: <PendingIcon />, color: 'warning' },
  { label: 'تایید شده', value: StoreEditRequestStatus.APPROVED, icon: <CheckCircleIcon />, color: 'success' },
  { label: 'رد شده', value: StoreEditRequestStatus.REJECTED, icon: <CancelIcon />, color: 'error' },
  { label: 'لغو شده', value: StoreEditRequestStatus.CANCELLED, icon: <RemoveCircleOutlineIcon />, color: '' },
];

const StoreEditRequestsManagement: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [editRequests, setEditRequests] = useState<IStoreEditRequest[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedRequest, setSelectedRequest] = useState<IStoreEditRequest | null>(null);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [rejectionOpen, setRejectionOpen] = useState<boolean>(false);
  const [approvalOpen, setApprovalOpen] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [approvalNote, setApprovalNote] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [changes, setChanges] = useState<ChangeItem[]>([]);
  const [history, setHistory] = useState<EditRequestSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  // Scroll state for tabs
  const tabsRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  // Image helpers
  const PLACEHOLDER_IMG = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';
  const isDataUrl = (s: string) => s.startsWith('data:image');
  const isLikelyImageUrl = (s: string) => /\.(jpe?g|png|gif|webp|svg|bmp|ico)(\?.*)?$/i.test(s) || s.startsWith('http');
  const isRawBase64Image = (s: string) => {
    if (!s || typeof s !== 'string') return false;
    const t = s.replace(/\s+/g, '');
    const looksBase64 = /^[A-Za-z0-9+/]+={0,2}$/.test(t);
    const magic = t.startsWith('/9j/') || t.startsWith('iVBOR') || t.startsWith('R0lGOD') || t.startsWith('UklGR');
    return (looksBase64 || magic) && t.length > 500 && !isDataUrl(t);
  };
  const toDataUrl = (b64: string) => `data:image/jpeg;base64,${b64}`;

  const resolveImageSrc = (val: any): string => {
    try {
      if (!val) return PLACEHOLDER_IMG;
      if (typeof val === 'string') {
        const s = val.trim();
        if (isDataUrl(s) || isLikelyImageUrl(s)) return s;
        if (isRawBase64Image(s)) return toDataUrl(s);
        return s;
      }
      if (typeof val === 'object') {
        if ((val as any).url) return String((val as any).url);
        if ((val as any).path) return String((val as any).path);
      }
      return PLACEHOLDER_IMG;
    } catch { return PLACEHOLDER_IMG; }
  };

  const isImageContent = (value: any): boolean => {
    try {
      if (!value) return false;
      if (typeof value === 'string') {
        const s = value.trim();
        return isDataUrl(s) || isLikelyImageUrl(s) || isRawBase64Image(s);
      }
      if (Array.isArray(value)) {
        return value.some(v => typeof v === 'string' ? isImageContent(v) : (v && (v.url || v.path)));
      }
      if (typeof value === 'object') return Boolean((value as any)?.url || (value as any)?.path);
      return false;
    } catch { return false; }
  };

  // Check scroll state
  const updateScrollState = useCallback(() => {
    if (tabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
      setScrollState({
        left: scrollLeft > 5,
        right: scrollLeft < scrollWidth - clientWidth - 5
      });
    }
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState]);

  // Fetch edit requests
  const fetchEditRequests = async (pageNum: number, status?: StoreEditRequestStatus) => {
    setLoading(true);
    try {
      const response = await storeEditRequestService.getAllEditRequests(pageNum, 10, status);
      if (response.success && response.data) {
        setEditRequests(response.data.docs);
        setTotalPages(response.data.totalPages);
        setPage(pageNum);
      } else {
        setEditRequests([]);
        setTotalPages(1);
        setPage(1);
      }
    } catch (error: any) {
      console.error('Error fetching edit requests:', error?.response?.status, error?.response?.data, error);
      const status = error?.response?.status;
      const serverMsg = error?.response?.data?.message;
      const msg = status === 403
        ? 'دسترسی به این بخش ندارید (manage_stores). با مدیر ارشد تماس بگیرید.'
        : status === 401
          ? 'نشست منقضی شده — لطفاً دوباره وارد شوید.'
          : serverMsg || `خطا در دریافت درخواست‌های ویرایش${status ? ` (${status})` : ''}`;
      enqueueSnackbar(msg, { variant: 'error' });
      setEditRequests([]);
      setTotalPages(1);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchEditRequests(1);
    }
  }, [isAuthenticated, authLoading]);

  // ✅ Fetch edit request details — backend already produces rich diff. No frontend extraction.
  const fetchEditRequestDetails = async (requestId: string) => {
    try {
      setDetailsLoading(true);
      setChanges([]);
      const resp = await storeEditRequestService.getEditRequestDetails(requestId);
      const payload = (resp as any)?.data || {};
      const editRequest = payload.editRequest || payload;
      if (editRequest) setSelectedRequest((prev) => ({ ...(prev as any), ...editRequest }));
      const diff: ChangeItem[] = Array.isArray(payload.changes) ? payload.changes : [];
      setChanges(diff.filter(c => c && c.hasChanged !== false));

      // Load full history for the same store
      const storeIdRaw: any = editRequest?.storeId;
      const storeId = typeof storeIdRaw === 'string' ? storeIdRaw : storeIdRaw?._id;
      if (storeId) fetchEditHistory(storeId);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در دریافت جزئیات تغییرات', { variant: 'error' });
    } finally {
      setDetailsLoading(false);
    }
  };

  // ✅ Fetch full edit-request history for this store
  const fetchEditHistory = async (storeId: string) => {
    try {
      setHistoryLoading(true);
      const resp = await storeEditRequestService.getEditRequests({ storeId, limit: 50 });
      const data: any = (resp as any)?.data || resp;
      const docs: EditRequestSummary[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.docs)
        ? data.docs
        : [];
      // Normalize status casing — store uses lowercase, timeline expects uppercase
      const normalized: EditRequestSummary[] = docs.map((d: any) => ({
        ...d,
        status: String(d.status || '').toUpperCase() as any,
        approvedBy: d.approvedBy
          ? { name: [d.approvedBy.firstName, d.approvedBy.lastName].filter(Boolean).join(' ') || d.approvedBy.email }
          : undefined,
        rejectedBy: d.rejectedBy
          ? { name: [d.rejectedBy.firstName, d.rejectedBy.lastName].filter(Boolean).join(' ') || d.rejectedBy.email }
          : undefined,
        createdBy: d.createdBy
          ? { name: [d.createdBy.firstName, d.createdBy.lastName].filter(Boolean).join(' ') || d.createdBy.email }
          : undefined,
      }));
      setHistory(normalized);
    } catch (e) {
      console.error('Error fetching store edit history:', e);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    fetchEditRequests(1, tabs[index].value);
  };

  const handleViewDetails = (request: IStoreEditRequest) => {
    setSelectedRequest(request);
    setDetailsOpen(true);
    fetchEditRequestDetails(request._id);
  };

  const handleApproveEditRequest = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await storeEditRequestService.approveEditRequest(selectedRequest._id, approvalNote);
      enqueueSnackbar('درخواست ویرایش با موفقیت تایید شد', { variant: 'success' });
      setApprovalOpen(false);
      setApprovalNote('');
      fetchEditRequests(page, tabs[activeTab].value);
    } catch (error: any) {
      console.error('Error approving edit request:', error);
      enqueueSnackbar('خطا در تایید درخواست ویرایش', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectEditRequest = async () => {
    if (!selectedRequest || !rejectionReason.trim()) return;
    setActionLoading(true);
    try {
      await storeEditRequestService.rejectEditRequest(selectedRequest._id, rejectionReason);
      enqueueSnackbar('درخواست ویرایش با موفقیت رد شد', { variant: 'success' });
      setRejectionOpen(false);
      setRejectionReason('');
      fetchEditRequests(page, tabs[activeTab].value);
    } catch (error: any) {
      console.error('Error rejecting edit request:', error);
      enqueueSnackbar('خطا در رد درخواست ویرایش', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: Date | undefined) => {
    if (!dateString) return 'نامشخص';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusText = (status: StoreEditRequestStatus) => {
    switch (status) {
      case StoreEditRequestStatus.PENDING: return 'در انتظار بررسی';
      case StoreEditRequestStatus.APPROVED: return 'تایید شده';
      case StoreEditRequestStatus.REJECTED: return 'رد شده';
      case StoreEditRequestStatus.CANCELLED: return 'لغو شده';
      default: return 'نامشخص';
    }
  };

  const statusChipColors: Record<string, { bg: string; fg: string }> = {
    [StoreEditRequestStatus.PENDING]: { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning-dark)' },
    [StoreEditRequestStatus.APPROVED]: { bg: 'var(--color-success-bg)', fg: 'var(--color-success-dark)' },
    [StoreEditRequestStatus.REJECTED]: { bg: 'var(--color-error-bg)', fg: 'var(--color-error-dark)' },
    [StoreEditRequestStatus.CANCELLED]: { bg: 'var(--color-bg-default)', fg: 'var(--color-text-secondary)' },
  };

  // ✅ extractChanges حذف شد — diff از بک‌اند دریافت می‌شود.
  // ✅ renderValue حذف شد — کامپوننت‌های اختصاصی (ChangesList → SimpleDiff/CapacityDiff/ImagesDiff/TextDiff) جایگزین شده‌اند.

  return (
    <>
      <Helmet>
        <title>مدیریت درخواست‌های ویرایش فروشگاه</title>
      </Helmet>

      <div className="edit-requests-page">
        <MyShiftStatusBanner category="store_edit" />
        {/* Header */}
        <div className="edit-requests-header">
          <h1>
            <StoreIcon />
            مدیریت درخواست‌های ویرایش فروشگاه
          </h1>
          <div className="header-actions">
            <button
              className="header-btn refresh"
              onClick={() => fetchEditRequests(page, tabs[activeTab].value)}
              disabled={loading}
            >
              <RefreshIcon />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className={`edit-requests-tabs-wrapper ${scrollState.left ? 'scroll-left' : ''} ${scrollState.right ? 'scroll-right' : ''}`}>
          <div className="edit-requests-tabs" ref={tabsRef} onScroll={updateScrollState}>
            {tabs.map((tab, index) => (
              <button
                key={index}
                className={`edit-tab ${activeTab === index ? `active ${tab.color}` : ''}`}
                onClick={() => handleTabChange(index)}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="edit-requests-table-wrapper">
          {(authLoading || loading) && !editRequests.length ? (
            <div className="loading-overlay">
              <CircularProgress size={40} />
            </div>
          ) : editRequests.length === 0 ? (
            <div className="empty-state">
              <StoreIcon />
              <p>هیچ درخواست ویرایشی یافت نشد</p>
              <button className="header-btn" style={{ marginTop: 12 }} onClick={() => fetchEditRequests(page, tabs[activeTab].value)}>
                <RefreshIcon fontSize="small" /> بررسی مجدد
              </button>
            </div>
          ) : (
            <>
              <table className="edit-requests-table">
                <thead>
                  <tr>
                    <th>فروشگاه</th>
                    <th>وضعیت</th>
                    <th>مسئول</th>
                    <th>تاریخ ایجاد</th>
                    <th>ایجاد کننده</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {editRequests.map((request) => (
                    <tr key={request._id} onClick={() => handleViewDetails(request)}>
                      <td>
                        <div className="store-cell">
                          {request.storeId.logo ? (
                            <img
                              src={request.storeId.logo}
                              alt={request.storeId.name}
                              className="store-thumb"
                              onError={(e: any) => { e.target.src = PLACEHOLDER_IMG; }}
                            />
                          ) : (
                            <div className="store-thumb-placeholder">
                              {request.storeId.name.charAt(0)}
                            </div>
                          )}
                          <span className="store-name">{request.storeId.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${request.status}`}>
                          {getStatusText(request.status)}
                        </span>
                      </td>
                      <td><AssigneeCell value={(request as any).assignedTo} /></td>
                      <td>{formatDate(request.createdAt)}</td>
                      <td>{request.createdBy?.firstName} {request.createdBy?.lastName}</td>
                      <td>
                        <div className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          {request.status === StoreEditRequestStatus.PENDING && (
                            <>
                              <button
                                className="action-btn approve"
                                title="تایید درخواست"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setApprovalOpen(true);
                                }}
                              >
                                <CheckIcon fontSize="small" />
                              </button>
                              <button
                                className="action-btn reject"
                                title="رد درخواست"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setRejectionOpen(true);
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </button>
                            </>
                          )}
                          <button
                            className="action-btn"
                            title="مشاهده جزئیات"
                            onClick={() => handleViewDetails(request)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="edit-requests-pagination">
                  <div className="pagination-info">
                    صفحه {page} از {totalPages}
                  </div>
                  <div className="pagination-btns">
                    <button
                      className="pagination-btn"
                      disabled={page === 1}
                      onClick={() => fetchEditRequests(page - 1, tabs[activeTab].value)}
                    >
                      قبلی
                    </button>
                    <button
                      className="pagination-btn"
                      disabled={page === totalPages}
                      onClick={() => fetchEditRequests(page + 1, tabs[activeTab].value)}
                    >
                      بعدی
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Mobile Cards */}
        {!loading && editRequests.length > 0 && (
          <div className="edit-requests-cards">
            {editRequests.map((request) => (
              <div key={request._id} className="edit-request-card" onClick={() => handleViewDetails(request)}>
                <div className="edit-request-card-header">
                  {request.storeId.logo ? (
                    <img
                      src={request.storeId.logo}
                      alt={request.storeId.name}
                      className="edit-request-card-thumb"
                      onError={(e: any) => { e.target.src = PLACEHOLDER_IMG; }}
                    />
                  ) : (
                    <div className="edit-request-card-thumb-placeholder">
                      {request.storeId.name.charAt(0)}
                    </div>
                  )}
                  <div className="edit-request-card-info">
                    <div className="edit-request-card-title">{request.storeId.name}</div>
                  </div>
                </div>
                <div className="edit-request-card-row">
                  <span className="edit-request-card-label">وضعیت</span>
                  <span className={`status-badge ${request.status}`}>
                    {getStatusText(request.status)}
                  </span>
                </div>
                <div className="edit-request-card-row">
                  <span className="edit-request-card-label">تاریخ</span>
                  <span className="edit-request-card-value">{formatDate(request.createdAt)}</span>
                </div>
                <div className="edit-request-card-actions" onClick={(e) => e.stopPropagation()}>
                  {request.status === StoreEditRequestStatus.PENDING && (
                    <>
                      <button
                        className="action-btn approve"
                        onClick={() => {
                          setSelectedRequest(request);
                          setApprovalOpen(true);
                        }}
                      >
                        <CheckIcon fontSize="small" />
                      </button>
                      <button
                        className="action-btn reject"
                        onClick={() => {
                          setSelectedRequest(request);
                          setRejectionOpen(true);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    </>
                  )}
                  <button className="action-btn" onClick={() => handleViewDetails(request)}>
                    <VisibilityIcon fontSize="small" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Details Dialog (rewritten 2026-04-29 with MUI + ChangesList + EditHistoryTimeline) */}
      <Dialog
        open={detailsOpen && !!selectedRequest}
        onClose={() => setDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
        dir="rtl"
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh' } }}
      >
        {selectedRequest && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                pb: 1.5,
                borderBottom: '1px solid var(--color-divider)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityIcon fontSize="small" />
                <Box>
                  <Typography component="div" sx={{ fontWeight: 700, fontSize: 17 }}>
                    جزئیات درخواست ویرایش فروشگاه
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                    کد: {selectedRequest._id?.slice(-6)}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={() => setDetailsOpen(false)} size="small" sx={{ mt: -0.5 }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2.5, pb: 1 }}>
              {/* Store header card */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  border: '1px solid var(--color-border)',
                  borderRadius: 2,
                  mb: 2.5,
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
              >
                {selectedRequest.storeId?.logo ? (
                  <Box
                    component="img"
                    src={resolveImageSrc(selectedRequest.storeId.logo)}
                    alt={selectedRequest.storeId.name}
                    onError={(e: any) => { e.target.src = PLACEHOLDER_IMG; }}
                    sx={{ width: 72, height: 72, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 72, height: 72, borderRadius: 1.5,
                      background: 'var(--color-bg-default)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 28, fontWeight: 700, color: 'var(--color-text-secondary)', flexShrink: 0,
                    }}
                  >
                    {selectedRequest.storeId?.name?.charAt(0) || '?'}
                  </Box>
                )}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap" useFlexGap>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
                      {selectedRequest.storeId?.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={getStatusText(selectedRequest.status)}
                      sx={{
                        background: statusChipColors[selectedRequest.status]?.bg || 'var(--color-bg-default)',
                        color: statusChipColors[selectedRequest.status]?.fg || 'var(--color-text-secondary)',
                        fontWeight: 600,
                      }}
                    />
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', display: 'block', lineHeight: 1.8 }}>
                    <strong>درخواست‌کننده:</strong>{' '}
                    {[selectedRequest.createdBy?.firstName, selectedRequest.createdBy?.lastName].filter(Boolean).join(' ') || 'نامشخص'}
                    {' • '}
                    <strong>تاریخ:</strong> {formatDate(selectedRequest.createdAt)}
                  </Typography>
                </Box>

                {selectedRequest.status === StoreEditRequestStatus.PENDING && (
                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => { setDetailsOpen(false); setApprovalOpen(true); }}
                    >
                      تایید
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={() => { setDetailsOpen(false); setRejectionOpen(true); }}
                    >
                      رد
                    </Button>
                  </Stack>
                )}
              </Box>

              {/* Changes section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CompareArrowsIcon fontSize="small" sx={{ color: 'var(--color-text-secondary)' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  تغییرات درخواست شده {changes.length > 0 && `(${changes.length} مورد)`}
                </Typography>
              </Box>
              {detailsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <ChangesList changes={changes} />
              )}

              {/* Status info banners */}
              {selectedRequest.status === StoreEditRequestStatus.APPROVED && selectedRequest.approvalNote && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                    یادداشت تایید — توسط {[selectedRequest.approvedBy?.firstName, selectedRequest.approvedBy?.lastName].filter(Boolean).join(' ') || 'ادمین'} در {formatDate(selectedRequest.approvedAt)}:
                  </Typography>
                  {selectedRequest.approvalNote}
                </Alert>
              )}
              {selectedRequest.status === StoreEditRequestStatus.REJECTED && selectedRequest.rejectionReason && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                    دلیل رد — توسط {[selectedRequest.rejectedBy?.firstName, selectedRequest.rejectedBy?.lastName].filter(Boolean).join(' ') || 'ادمین'} در {formatDate(selectedRequest.rejectedAt)}:
                  </Typography>
                  {selectedRequest.rejectionReason}
                </Alert>
              )}

              <Divider sx={{ my: 2.5 }} />

              {/* History timeline */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <HistoryIcon fontSize="small" sx={{ color: 'var(--color-text-secondary)' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  تاریخچهٔ ویرایش‌های این فروشگاه {history.length > 0 && `(${history.length})`}
                </Typography>
              </Box>
              {historyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <EditHistoryTimeline history={history} />
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid var(--color-divider)', gap: 1 }}>
              <Button onClick={() => setDetailsOpen(false)}>بستن</Button>
              <Box sx={{ flex: 1 }} />
              {selectedRequest.status === StoreEditRequestStatus.PENDING && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => { setDetailsOpen(false); setRejectionOpen(true); }}
                  >
                    رد درخواست
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => { setDetailsOpen(false); setApprovalOpen(true); }}
                  >
                    تایید درخواست
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Approval Dialog */}
      {approvalOpen && selectedRequest && (
        <div className="dialog-overlay" onClick={() => !actionLoading && setApprovalOpen(false)}>
          <div className="dialog small" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header success">
              <h2><CheckCircleIcon /> تایید درخواست</h2>
              <button className="dialog-close" onClick={() => !actionLoading && setApprovalOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="dialog-content">
              <p className="confirm-text">تایید درخواست ویرایش</p>
              <p className="confirm-hint">تغییرات درخواستی بر روی فروشگاه اعمال خواهد شد.</p>

              <div className="confirm-box success">
                <StoreIcon />
                <strong>{selectedRequest.storeId.name}</strong>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                  یادداشت (اختیاری)
                </label>
                <textarea
                  className="form-textarea"
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                  disabled={actionLoading}
                  placeholder="یادداشت برای فروشنده..."
                  rows={3}
                />
              </div>
            </div>
            <div className="dialog-actions">
              <button
                className="dialog-btn cancel"
                onClick={() => setApprovalOpen(false)}
                disabled={actionLoading}
              >
                انصراف
              </button>
              <button
                className="dialog-btn success"
                onClick={handleApproveEditRequest}
                disabled={actionLoading}
              >
                {actionLoading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                تایید
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Dialog */}
      {rejectionOpen && selectedRequest && (
        <div className="dialog-overlay" onClick={() => !actionLoading && setRejectionOpen(false)}>
          <div className="dialog small" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header error">
              <h2><CancelIcon /> رد درخواست</h2>
              <button className="dialog-close" onClick={() => !actionLoading && setRejectionOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="dialog-content">
              <p className="confirm-text">رد درخواست ویرایش</p>
              <p className="confirm-hint">تغییرات درخواستی اعمال نخواهد شد.</p>

              <div className="confirm-box error">
                <StoreIcon />
                <strong>{selectedRequest.storeId.name}</strong>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                  دلیل رد <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <textarea
                  className={`form-textarea ${rejectionReason.trim() === '' ? 'error' : ''}`}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  disabled={actionLoading}
                  placeholder="دلیل رد..."
                  rows={3}
                />
                {rejectionReason.trim() === '' && (
                  <p className="form-error">دلیل رد الزامی است</p>
                )}
              </div>
            </div>
            <div className="dialog-actions">
              <button
                className="dialog-btn cancel"
                onClick={() => setRejectionOpen(false)}
                disabled={actionLoading}
              >
                انصراف
              </button>
              <button
                className="dialog-btn error"
                onClick={handleRejectEditRequest}
                disabled={actionLoading || rejectionReason.trim() === ''}
              >
                {actionLoading ? <CircularProgress size={16} color="inherit" /> : <CloseIcon />}
                رد
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StoreEditRequestsManagement;
